/* eslint-disable @typescript-eslint/no-explicit-any */
import { atom, useSetAtom } from 'jotai';
import { atomWithReset, atomWithStorage, useResetAtom } from 'jotai/utils';
import callApi from 'lib/callApi';
import { companyIdAtom } from 'views/state';

/**
 * Interface for CA Activation Key data
 */
export interface ActivationKey {
  id: string;
  name: string;
  value: string;
}

/**
 * Interface for CA Activation Keys state
 */
export interface ActivationKeysState {
  results: ActivationKey[];
  count: number;
  loading: boolean;
  error: string | null;
}

/**
 * Initial state for activation keys
 */
const initialState: ActivationKeysState = {
  results: [],
  count: 0,
  loading: false,
  error: null,
};

/**
 * Default column keys for the activation keys DataGrid
 */
export const DEFAULT_COLUMN_KEYS_ACTIVATION_KEYS = [
  'name',
  'value',
  'type',
  'actions',
];

/**
 * Atom for storing activation keys filter state
 */
export const activationKeysFilterAtom = atomWithStorage(
  'activationKeysFilter',
  {
    search: '',
    type: 'all', // 'all', 'Servers', or 'Workstations'
  },
);

/**
 * Atom for storing filter values from SearchAndFilters component
 */
export const filterValuesAtom = atomWithReset<Record<string, any>>({});

/**
 * Atom for pagination configuration
 */
export const paginationConfigAtom = atomWithReset({
  page_size: 10, // Default page size
  page: 1,
});

/**
 * Atom for orderby field
 */
export const orderbyAtom = atomWithReset<string>('name');

/**
 * Atom for sort order
 */
export const sortOrderAtom = atomWithReset<'desc' | 'asc'>('asc');

/**
 * Atom for forcing rerender of the activation keys grid
 */
export const forceRerenderActivationKeysGridAtom = atom<string>('');

/**
 * Atom for activation keys grid data refresh
 */
export const activationKeysGridDataRefreshAtom = atom<number>(0);

/**
 * Atom for query parameters to fetch activation keys
 */
export const activationKeysQueryParamsAtom = atom(async (get) => {
  const companyId = get(companyIdAtom);
  const paginationConfig = get(paginationConfigAtom);
  const orderBy = get(orderbyAtom);
  const order = get(sortOrderAtom);
  const ordering = `${order === 'desc' ? '-' : ''}${orderBy}`;

  // Get the refresh trigger value to force re-evaluation when refreshed
  const _activationKeysGridDataRefresh = get(activationKeysGridDataRefreshAtom);

  if (companyId && companyId !== -1) {
    const params: Record<string, string> = {
      company_id: companyId.toString(),
      page: paginationConfig.page.toString(),
      page_size: paginationConfig.page_size.toString(),
      ordering: ordering,
    };

    return params;
  }

  return null;
});

/**
 * Atom for storing activation keys data
 */
export const activationKeysDataAtom = atom<ActivationKeysState>(initialState);

/**
 * Atom for fetching activation keys data
 */
export const fetchActivationKeysAtom = atom(
  (get) => get(activationKeysDataAtom),
  async (get, set, companyId: string) => {
    set(activationKeysDataAtom, {
      ...get(activationKeysDataAtom),
      loading: true,
    });

    try {
      const response = await callApi(
        `cloud_agents/activation-keys/${companyId}/`,
        'GET',
        {}, // urlParams
        null, // data
        null, // headers
      );

      if (response.status === 200 || response.status === 201) {
        const data = response.data;
        const { _customerId, ...keysData } = data;

        // Transform the data to match the ActivationKey interface
        const activationKeys = Object.entries(keysData)
          .filter(([key]) => key.toLowerCase() !== 'customerid') // Filter out CustomerId entries
          .map(([key, value]) => ({
            id: key, // Using the key as the ID
            name: key.replace(/^./, (str) => str.toUpperCase()), // Format name
            value: value as string,
            // Determine type based on name
            type: key.toLowerCase().includes('server')
              ? 'Servers'
              : key.toLowerCase().includes('workstation')
                ? 'Workstations'
                : '',
          }));

        set(activationKeysDataAtom, {
          results: activationKeys,
          count: activationKeys.length,
          loading: false,
          error: null,
        });
      } else {
        set(activationKeysDataAtom, {
          ...initialState,
          loading: false,
          error: 'Failed to fetch activation keys',
        });
      }
    } catch (error) {
      set(activationKeysDataAtom, {
        ...initialState,
        loading: false,
        error:
          (error as Error).message ||
          'An error occurred while fetching activation keys',
      });
    }
  },
);

/**
 * Atom for filtered activation keys based on search and type filters
 */
export const filteredActivationKeysAtom = atom((get) => {
  const { results } = get(activationKeysDataAtom);
  const filterValues = get(filterValuesAtom);

  // Get search term and type filter values
  const search = filterValues.search || '';
  const searchString = String(search).toLowerCase(); // Ensure search is a string and convert to lowercase
  const typeArray = filterValues.type || ['all'];
  const type = typeArray.length > 0 ? typeArray[0] : 'all';

  return results.filter((key) => {
    // Apply search filter
    const matchesSearch = !searchString
      ? true
      : key.name.toLowerCase().includes(searchString) ||
        key.value.toLowerCase().includes(searchString);

    // Apply type filter based on key name
    const matchesType =
      type === 'all' ||
      (type === 'Servers' && key.name.toLowerCase().includes('server')) ||
      (type === 'Workstations' &&
        key.name.toLowerCase().includes('workstation'));

    // Return true if both filters match
    return matchesSearch && matchesType;
  });
});

/**
 * Function to reset atoms when filter changes
 * Used by the Filters component to reset pagination and sorting when filters change
 */
export function useOnFilterChangeResetAtoms() {
  const resetPaginationConfig = useSetAtom(paginationConfigAtom);

  const resetAtoms = () => {
    // Reset pagination to first page when filters change
    resetPaginationConfig(() => ({
      page: 1,
      page_size: 10,
    }));
  };

  return resetAtoms;
}

export function useOnCompanyChangeActivationTabConfigResetAtoms() {
  const resetFilterValuesAtom = useOnFilterChangeResetAtoms();
  const resetFilterValues = useResetAtom(filterValuesAtom);

  const resetAtoms = () => {
    resetFilterValuesAtom();
    resetFilterValues();
  };

  return resetAtoms;
}
